import { useState } from 'react';
import { signInWithMicrosoft, type MsIntent } from '@/lib/microsoft-auth';

interface MicrosoftSignInButtonProps {
  /** Which login page this button lives on (messaging hint only). */
  intent: MsIntent;
}

/**
 * "Sign in with Microsoft" — rendered per Microsoft's brand guidelines
 * (white surface, four-square mark, Segoe UI label). Starts the OAuth
 * redirect; on success the browser leaves the page, so we only surface an
 * error if the redirect fails to start.
 */
export function MicrosoftSignInButton({ intent }: MicrosoftSignInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    setIsLoading(true);
    setError('');
    const { error: startError } = await signInWithMicrosoft(intent);
    if (startError) {
      setError("Couldn't start Microsoft sign-in. Please try again.");
      setIsLoading(false);
    }
    // Success → the browser is already redirecting to Microsoft.
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        aria-label="Sign in with Microsoft"
        className="w-full h-11 flex items-center justify-center gap-2.5 rounded-lg border border-[#8C8C8C] bg-white text-[#5E5E5E] text-sm font-semibold transition-colors hover:bg-[#F3F3F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F4E79] disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }}
      >
        <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
        {isLoading ? 'Redirecting…' : 'Sign in with Microsoft'}
      </button>
      {error && (
        <p className="mt-2 text-sm text-destructive text-center">{error}</p>
      )}
    </div>
  );
}
